import { DocumentOrganizationKeys } from '@griply/types';
import { buildTagReorderUpdate } from '@griply/utils';
import {
  getDefaultFilterDocument,
  getFilterDocument,
  useTagCustomOrderByGrouping,
  useUpdateTag,
} from 'features/tags';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { UpdateTask } from 'shared/types/task';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

type TasksOverviewProps = {
  scrollElement?: React.RefObject<HTMLDivElement>;
  tag: Tag;
};

export const TasksOverview: React.FC<TasksOverviewProps> = ({
  scrollElement,
  tag,
}) => {
  const { submit: updateTag } = useUpdateTag();
  const { submit: updateTasks } = useUpdateTasks();
  const updateRelationships = useUpdateRelationshipsReferences();

  const filterDocument = useMemo(() => getFilterDocument(tag), [tag]);

  const groupSettings = useMemo(
    () => ({
      [GroupByMode.Section]: { tagId: tag.id },
      [GroupByMode.Goal]: { tagId: tag.id },
      [GroupByMode.LifeArea]: { tagId: tag.id },
      [GroupByMode.DayPart]: { tagId: tag.id },
      [GroupByMode.Priority]: { tagId: tag.id },
      [GroupByMode.Type]: { tagId: tag.id },
      [GroupByMode.Deadline]: { tagId: tag.id },
      [GroupByMode.DeadlineDeadline]: { tagId: tag.id },
      [GroupByMode.Tag]: { tagId: tag.id },
      [GroupByMode.None]: { tagId: tag.id },
    }),
    [tag.id],
  );

  const onSectionCollapse = useCallback(
    (collapsedSections: ID[]) => {
      if (!tag) {
        return;
      }

      updateTag({
        ...tag,
        filterDocument: {
          ...(tag.filterDocument ?? {}),
          collapsedSections,
        },
      });
    },
    [tag, updateTag],
  );

  const onReset = () =>
    updateTag({
      ...tag,
      filterDocument: {
        ...getDefaultFilterDocument(tag),
        collapsedSections: tag.filterDocument?.collapsedSections ?? null,
      },
    });

  const onReorder = useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: UpdateTask[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      if (!tag) {
        return;
      }

      updateTasks(updatedTasks);

      // Strip the inline new-task sentinel before persisting; only web emits it.
      const sanitizedSections = updatedSections.map((section) => ({
        ...section,
        taskSorting: section.taskSorting.filter((id) => id !== 'new-task'),
      }));

      const tagUpdate = buildTagReorderUpdate({
        tag,
        updatedSections: sanitizedSections,
        groupBy: filterDocument[DocumentOrganizationKeys.GroupByMode],
      });
      if (tagUpdate) {
        updateTag({ ...tag, ...tagUpdate });
      }

      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [filterDocument, tag, updateTag, updateRelationships, updateTasks],
  );

  const customOrder = useTagCustomOrderByGrouping(
    filterDocument.groupByMode,
    tag,
  );

  if (!tag) {
    return null;
  }

  return (
    <ProvidedTaskFilterList
      filterDocument={filterDocument}
      defaultFilterDocument={getDefaultFilterDocument(tag)}
      customOrder={customOrder}
      groupSettings={groupSettings}
      onReorder={onReorder}
      onSectionCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      onReset={onReset}
    />
  );
};
